import { Device, Document, DocumentAnswer } from "../stores/models";
import { Template, TemplateConfig, TemplateQuestion } from "../types";

import Handlebars from "handlebars";
import { chain, orderBy } from "lodash";
import {
  getLatestDocumentVersion,
  hasElectrical,
  hasFirmware,
  hasHardware,
  hasImplantableComponent,
  hasSoftware,
  hasSterileComponent,
  isCanada,
  isFDA,
  isIVDR,
  isMDR,
  isSoftwareOnly,
  isUK,
} from ".";

// Register handlebars helpers
Handlebars.registerHelper("ifEquals", (arg1, arg2, options) => {
  return arg1 == arg2 ? options.fn() : options.inverse();
});

const reduceOp = function (args: any, reducer: (a: any, b: any) => boolean) {
  args = Array.from(args);
  args.pop(); // => options
  var first = args.shift();
  return args.reduce(reducer, first);
};

Handlebars.registerHelper({
  eq: function () {
    return reduceOp(arguments, (a, b) => a === b);
  },
  ne: function () {
    return reduceOp(arguments, (a, b) => a !== b);
  },
  lt: function () {
    return reduceOp(arguments, (a, b) => a < b);
  },
  gt: function () {
    return reduceOp(arguments, (a, b) => a > b);
  },
  lte: function () {
    return reduceOp(arguments, (a, b) => a <= b);
  },
  gte: function () {
    return reduceOp(arguments, (a, b) => a >= b);
  },
  and: function () {
    return reduceOp(arguments, (a, b) => a && b);
  },
  or: function () {
    return reduceOp(arguments, (a, b) => a || b);
  },
  not: function (value: unknown) {
    return !value;
  },
});

export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Loads all answers. Removes answers that do not have a config.
 * @param documents
 * @param orgId
 * @param templatesConfig
 * @returns
 */
export const loadDependencies = (
  documents: Document[],
  templateConfigs: TemplateConfig,
  currentTemplateConfig: Template
): Partial<Record<string, DocumentAnswer>> => {
  const data: Partial<Record<string, DocumentAnswer>> = {};
  // const dependencyConfig = templatesConfig[type].dependencies;

  // TODO test that this function returns an empty object if there are no dependencies
  // if (!dependencyConfig) return {};

  // get all document answers in one array
  const allAnswers = documents.flatMap(
    (d) => getLatestDocumentVersion(d)?.answers || []
  );

  // make sure we have only one answer for each dependency. Because sometimes answers are migrated from one document to another, they might be duplicated since they continue to live in the old document. A good example is the expected-lifetime that was recently moved from the POST_MARKET_CLINICAL_FOLLOW_UP_PLAN to the USER_NEEDS document. If users answer it in the new document (USER_NEEDS) it would be potentially duplicated since it could also exist in the old location (POST_MARKET_CLINICAL_FOLLOW_UP_PLAN) if they answered it there before. The long term solution is to write proper migrations in the BE and make sure that for new doc version of the document where the answer was previously defined, the answer is not copied over anymore. This is because whenever we generate a new document version we always copy over all answers from the previous document version.
  // On the other hand, if the answer is not defined in the new document, we should use the answer from the old document.

  // If we have it in the new document and not in the old document use the answer from the new document
  // 1. check if the answer is defined multiple times
  // 2. If there is only one answer, use it
  // 3. Otherwise check for which document the the answer is defined in the doc config
  // 4. Use the answer from the document that is defined in the doc config

  const groupedAnswers = chain(allAnswers).groupBy("element").values().value();

  const answersWithConfig: DocumentAnswer[] = groupedAnswers
    .map((answers: DocumentAnswer[]): DocumentAnswer | undefined => {
      if (answers.length === 1) {
        return answers[0];
      } else {
        const elementConfig = Object.entries(templateConfigs).find(
          ([_, value]) =>
            value.questions.find((element) => element.id === answers[0].element)
        );

        if (!elementConfig) {
          return orderBy(answers, ["createdAt"], ["desc"])[0];
        }

        const relatedDocument = documents.find(
          (d) => d.templateKey === elementConfig[0]
        );

        if (!relatedDocument) {
          // If we cannot find the related document, we cannot find the answer then just use the latest answer that we have in the answers array
          return orderBy(answers, ["createdAt"], ["desc"])[0];
        }

        // TODO this does not handle records or multi doc. Not relevant for now since we right now only reference data keys from single entity documents but should be handled in the future.
        const relatedDocumentVersion =
          getLatestDocumentVersion(relatedDocument);

        if (!relatedDocumentVersion) {
          return orderBy(answers, ["createdAt"], ["desc"])[0];
        }

        const relatedAnswer = relatedDocumentVersion.answers.find(
          (a) => a.element === answers[0].element
        );

        if (!relatedAnswer) {
          // just return the latest answer
          return orderBy(answers, ["createdAt"], ["desc"])[0];
        } else {
          return relatedAnswer;
        }
      }
    })
    .filter((a) => a !== undefined)
    // make sure we exclude elements that are part of the current template config
    .filter((a) =>
      currentTemplateConfig.questions.every((q) => q.id !== a.element)
    );

  answersWithConfig.forEach((a) => {
    data[a.element] = a;
  });

  return data;
};

export const formatAnswer = async (
  element: TemplateQuestion,
  value: string = ""
): Promise<string> => {
  switch (element.element.type) {
    case "textField":
      return value;

    case "table":
      // Clean up. Make sure the markdown is formatted correctly with no leading or trailing spaces
      return value
        .split("\n")
        .filter((line) => line.trim() !== "")
        .map((line) => line.trim())
        .join("\n");

    case "fileUpload":
      return `![${element.id}](${value})`;

    default:
      return value;
  }
};

export const loadDeviceCharacteristics = (
  device: Device
): Record<string, boolean | string> => {
  return {
    "device-name": device.name,
    "device-description": device.description,
    "has-software": hasSoftware(device),
    "has-hardware": hasHardware(device),
    "is-software-only": isSoftwareOnly(device),
    "has-sterile-component": hasSterileComponent(device),
    "has-implantable-component": hasImplantableComponent(device),
    "has-electrical-component": hasElectrical(device),
    "has-firmware-component": hasFirmware(device),
    "is-mdr": isMDR(device),
    "is-fda": isFDA(device),
    "is-ivdr": isIVDR(device),
    "is-uk": isUK(device),
    "is-canada": isCanada(device),
  };
};
